import React from "react";
import {
    Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, Flex, Icon, List, ListIcon, ListItem,
    Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay,
    Spinner, Stack, Text, useColorModeValue,
} from "@chakra-ui/react";
import { MdSync, MdBuild } from "react-icons/md";

/**
 * Spinner shown during migration.
 * @returns {React.ReactElement}
 */
function MigrationProgress() {
    return (
        <Flex direction="column" align="center" gap="16px" py="24px">
            <Spinner size="xl" color="brand.500" thickness="4px" />
            <Text fontSize="md" fontWeight="600">Migration en cours...</Text>
            <Text fontSize="sm" color="secondaryGray.600" textAlign="center">
                Veuillez patienter, cette opération peut prendre quelques minutes
            </Text>
        </Flex>
    );
}

/**
 * Single stat row with label and value.
 * @param {object} props
 * @param {string} props.label - Row label
 * @param {string|number} props.value - Row value
 * @param {string} [props.color] - Optional color token for the value
 * @returns {React.ReactElement}
 */
function StatRow({ label, value, color }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Flex justify="space-between">
            <Text fontSize="sm" color={labelColor}>{label}</Text>
            <Text fontSize="sm" fontWeight="600" color={color}>{value}</Text>
        </Flex>
    );
}

/**
 * Detailed migration statistics box.
 * @param {object} props
 * @param {object} props.details - Migration details with counts
 * @returns {React.ReactElement}
 */
function MigrationDetails({ details }) {
    const bg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    return (
        <Box p="14px" bg={bg} borderRadius="12px">
            <Stack spacing="8px">
                <StatRow label="Total services" value={details.total_services} />
                <StatRow label="Migrés avec succès" value={details.successfully_migrated} color="green.500" />
                <StatRow label="Erreurs" value={details.errors_count} color={details.errors_count > 0 ? "red.500" : undefined} />
            </Stack>
        </Box>
    );
}

/**
 * Migration result display with success or error alert.
 * @param {object} props
 * @param {object} props.result - Migration result data
 * @returns {React.ReactElement}
 */
function MigrationResult({ result }) {
    if (result.error) {
        return (
            <Alert status="error" borderRadius="12px" alignItems="flex-start">
                <AlertIcon />
                <Box><AlertTitle fontSize="sm">Erreur</AlertTitle><AlertDescription fontSize="xs">{result.error}</AlertDescription></Box>
            </Alert>
        );
    }
    return (
        <Stack spacing="16px">
            <Alert status="success" borderRadius="12px" alignItems="flex-start">
                <AlertIcon />
                <Box><AlertTitle fontSize="sm">Migration réussie !</AlertTitle><AlertDescription fontSize="xs">{result.message}</AlertDescription></Box>
            </Alert>
            {result.details && <MigrationDetails details={result.details} />}
        </Stack>
    );
}

/**
 * Pre-migration info listing what the operation will do.
 * @param {object} props
 * @param {number} props.totalServices - Total number of services to migrate
 * @returns {React.ReactElement}
 */
function PreMigrationInfo({ totalServices }) {
    return (
        <Stack spacing="16px">
            <Alert status="info" borderRadius="12px"><AlertIcon /><Text fontSize="sm">Cette opération va :</Text></Alert>
            <List spacing="10px">
                <ListItem fontSize="sm"><ListIcon as={MdBuild} color="blue.400" />Supprimer l'index existant</ListItem>
                <ListItem fontSize="sm"><ListIcon as={MdBuild} color="blue.400" />Recréer l'index avec le bon mapping</ListItem>
                <ListItem fontSize="sm"><ListIcon as={MdSync} color="blue.400" />Migrer {totalServices} services</ListItem>
            </List>
            <Alert status="warning" borderRadius="12px" alignItems="flex-start">
                <AlertIcon />
                <Box>
                    <AlertTitle fontSize="sm">Attention</AlertTitle>
                    <AlertDescription fontSize="xs">
                        Cette opération peut prendre plusieurs minutes. La recherche sera temporairement indisponible.
                    </AlertDescription>
                </Box>
            </Alert>
        </Stack>
    );
}

/**
 * Content area showing pre-migration info, progress spinner, or results.
 * @param {object} props
 * @param {boolean} props.migrating - Whether migration is in progress
 * @param {object|null} props.migrationResult - Migration result data or null
 * @param {number} props.totalServices - Total number of services to migrate
 * @returns {React.ReactElement}
 */
function MigrationDialogBody({ migrating, migrationResult, totalServices }) {
    if (migrating) return <MigrationProgress />;
    if (migrationResult) return <MigrationResult result={migrationResult} />;
    return <PreMigrationInfo totalServices={totalServices} />;
}

/**
 * Dialog footer with cancel/confirm/close buttons.
 * @param {object} props
 * @param {boolean} props.migrating - Whether migration is in progress
 * @param {object|null} props.migrationResult - Migration result data or null
 * @param {Function} props.onClose - Callback to close the dialog
 * @param {Function} props.onConfirm - Callback to start the migration
 * @param {Function} props.onDismissResult - Callback to dismiss result and close
 * @returns {React.ReactElement|null}
 */
function MigrationDialogFooter({ migrating, migrationResult, onClose, onConfirm, onDismissResult }) {
    if (migrating) return null;
    if (migrationResult) {
        return <Button colorScheme="green" onClick={onDismissResult}>Fermer</Button>;
    }
    return (
        <>
            <Button variant="ghost" mr="12px" onClick={onClose}>Annuler</Button>
            <Button colorScheme="brand" leftIcon={<Icon as={MdSync} />} onClick={onConfirm}>Confirmer la migration</Button>
        </>
    );
}

/**
 * Confirmation dialog for ElasticSearch migration with progress and result display.
 * @param {object} props
 * @param {boolean} props.open - Whether the dialog is visible
 * @param {Function} props.onClose - Callback to close the dialog
 * @param {Function} props.onConfirm - Callback to start the migration
 * @param {boolean} props.migrating - Whether migration is in progress
 * @param {object|null} props.migrationResult - Migration result data or null
 * @param {Function} props.onDismissResult - Callback to dismiss result and close
 * @param {number} props.totalServices - Total number of services to migrate
 * @returns {React.ReactElement}
 */
function MigrationConfirmDialog({ open, onClose, onConfirm, migrating, migrationResult, onDismissResult, totalServices }) {
    return (
        <Modal isOpen={open} onClose={() => !migrating && onClose()} size="lg" isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Confirmer la migration</ModalHeader>
                {!migrating && <ModalCloseButton />}
                <ModalBody>
                    <MigrationDialogBody migrating={migrating} migrationResult={migrationResult} totalServices={totalServices} />
                </ModalBody>
                <ModalFooter>
                    <MigrationDialogFooter migrating={migrating} migrationResult={migrationResult}
                        onClose={onClose} onConfirm={onConfirm} onDismissResult={onDismissResult} />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default MigrationConfirmDialog;
